<script>
import { mapActions, mapState } from 'vuex'
import placeLetters from '../../lib/random-placement'
import { convertRemToPxValue } from '../../lib/convert-css-units'

import PostCell from './PostCell'

export default {
  name: 'PostFeed',
  components: { PostCell },
  props: {
    posts: { type: Array, default: () => [] },
    currentPostId: { type: String, default: null },
    processing: Boolean
  },

  data: () => ({
    textSize: 80,
    parentEl: null,
    parentW: null,
    parentH: null,
    letterScale: null,
    feedHeightStyle: null,
    titleLetterElement: null,
    // for pointer speed calculation
    _lastPointerX: null,
    _lastPointerY: null,
    _lastPointerTime: null,
    _lastVector: null,
    _reverseCount: 0,
    _firstReverseTime: null,
    _autoHideTimer: null,
    autoHideDelay: 2000, // ms
    _navStateChangeTimer: null,
    _lastNavStateChange: 0,
    navStateDebounceDelay: 100, // ms - prevent rapid nav state changes
    // Cached computed styles and measurements
    _cachedComputedStyle: null,
    _cachedHeaderHeight: null,
    _cachedLetterHeight: null,
    // Resize optimization
    _resizeRAF: null,
    _lastParentW: null
  }),

  computed: {
    ...mapState(['user', 'navState']),
    adminOrEditor () {
      return !!this.user && (this.user.role === 'admin' || this.user.role === 'editor')
    },

    points () {
      const rawPoints = placeLetters(this.posts.length, this.letterScale)
      if (!rawPoints.length) return []
      const scale = 1 // 1 / rawPoints[rawPoints.length - 1].y
      const allH = rawPoints[rawPoints.length - 1].y - rawPoints[0].y
      const hShift = allH <= 1 ? rawPoints[0].y * -1 : 0
      // Calculate minimum top padding to prevent letter cutoff (only if parentH is available)
      let minTopPadding = 0
      if (this.parentH) {
        const headerHeight = this.getHeaderHeight()
        const letterHeight = this.getLetterHeight()
        minTopPadding = (headerHeight + letterHeight * 0.5) / this.parentH // Convert to normalized units
      }
      return rawPoints.map((point, index) => {
        let adjustedY = point.y + hShift

        // Ensure first letter has enough top padding (only apply if we have valid parentH)
        if (index === 0 && this.parentH && adjustedY < minTopPadding) {
          adjustedY = minTopPadding
        }
        return {
          ...point,
          xW: Math.floor(this.parentW * point.x),
          yH: Math.floor(this.parentH * adjustedY),
          yScaled: adjustedY * scale
        }
      })
    }

  },
  created () {
  },
  async mounted () {
    this.parentEl = this.$refs['post-feed']
    this.setParentSizeVars()
    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('click', this.onGlobalClick, true)
    document.addEventListener('mouseenter', this.onPostCellMouseEnter, true)
    document.addEventListener('mouseleave', this.onPostCellMouseLeave, true)
    if (this.$refs['post-feed-wrap']) {
      this.$refs['post-feed-wrap'].addEventListener('scroll', this.onFeedScroll, { passive: true })
    }
    this.drawLines()
    this.scrollToActivePost()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('click', this.onGlobalClick, true)
    document.removeEventListener('mouseenter', this.onPostCellMouseEnter, true)
    document.removeEventListener('mouseleave', this.onPostCellMouseLeave, true)
    if (this.$refs['post-feed-wrap']) {
      this.$refs['post-feed-wrap'].removeEventListener('scroll', this.onFeedScroll)
    }
    this.clearAutoHideTimer()
    if (this._navStateChangeTimer) {
      clearTimeout(this._navStateChangeTimer)
      this._navStateChangeTimer = null
    }
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF)
      this._resizeRAF = null
    }
    this.clearLayoutCache()
  },

  watch: {
    processing () {
      if (!this.processing) this.setContentLoaded()
    },
    posts: {
      async handler (old, newVal) {
        if (old.length > 1 && newVal.length > 1) {
          this.updateNavState('on')
        }
        await this.$nextTick()
        await this.setFeedHeightStyle()
        await this.$nextTick()
        this.setParentSizeVars()
        this.drawLines()
        this.scrollToActivePost()
      },
      immediate: false
    },
    '$route.params.id' () {
      this.updateNavState('off')
      this.scrollToActivePost()
    }
  },

  methods: {
    ...mapActions(['showEditor', 'setContentLoaded', 'setNavState']),

    getComputedStyleCached () {
      if (!this._cachedComputedStyle) {
        this._cachedComputedStyle = getComputedStyle(document.documentElement)
      }
      return this._cachedComputedStyle
    },

    getHeaderHeight () {
      if (this._cachedHeaderHeight !== null) {
        return this._cachedHeaderHeight
      }
      const header = document.querySelector('.main-header')
      if (header) {
        const headerRect = header.getBoundingClientRect()
        this._cachedHeaderHeight = headerRect.height
      } else {
        const computedStyle = this.getComputedStyleCached()
        this._cachedHeaderHeight = convertRemToPxValue(computedStyle.getPropertyValue('--base-size'))
      }
      return this._cachedHeaderHeight
    },

    getLetterHeight () {
      if (this._cachedLetterHeight !== null) {
        return this._cachedLetterHeight
      }
      if (!this.parentComputedStyle) {
        this.parentComputedStyle = getComputedStyle(this.parentEl)
      }
      this._cachedLetterHeight = convertRemToPxValue(this.parentComputedStyle.getPropertyValue('--title-letter-font-size'))
      return this._cachedLetterHeight
    },

    clearLayoutCache () {
      this._cachedComputedStyle = null
      this._cachedHeaderHeight = null
      this._cachedLetterHeight = null
    },

    onWindowResize () {
      if (this._resizeRAF) {
        cancelAnimationFrame(this._resizeRAF)
      }
      this._resizeRAF = requestAnimationFrame(() => {
        this.handleResize()
        this._resizeRAF = null
      })
    },

    handleResize () {
      const parent = this.parentEl
      if (!parent) return

      const { width, height } = parent.getBoundingClientRect()
      const newParentW = width || window.innerWidth
      const newParentH = height || window.innerHeight
      if (this._lastParentW === null) {
        this._lastParentW = newParentW
        this.parentW = newParentW
        this.parentH = newParentH
        this.clearLayoutCache()
        const fontSize = this.getLetterHeight()
        this.letterScale = fontSize / this.parentH
        this.drawLines()
        return
      }
      const widthChanged = Math.abs(newParentW - this._lastParentW) > 5
      const heightChanged = Math.abs(newParentH - this.parentH) > 5
      // Skip if no significant change
      if (!widthChanged && !heightChanged) {
        return
      }
      // Update dimensions
      this.parentW = newParentW
      this.parentH = newParentH
      this._lastParentW = newParentW
      if (widthChanged && !heightChanged) {
        // Pure horizontal resize: only redraw canvas
        this.drawLines()
      } else if (!widthChanged && heightChanged) {
        // Pure vertical resize: update letterScale only (canvas auto-scales)
        this.clearLayoutCache()
        const fontSize = this.getLetterHeight()
        this.letterScale = fontSize / this.parentH
      } else {
        // Mixed resize: full recalculation
        this.clearLayoutCache()
        const fontSize = this.getLetterHeight()
        this.letterScale = fontSize / this.parentH
        this.drawLines()
      }
    },

    updateNavState (newState) {
      const now = performance.now()
      // Prevent rapid state changes that can cause hanging
      if (now - this._lastNavStateChange < this.navStateDebounceDelay) {
        return
      }
      this._lastNavStateChange = now
      this.setNavState(newState)
      // Clear any pending state change
      if (this._navStateChangeTimer) {
        clearTimeout(this._navStateChangeTimer)
        this._navStateChangeTimer = null
      }
      if (newState === 'off') {
        this.clearAutoHideTimer()
      }
    },

    // -------------------------------
    // Global interaction handlers
    // -------------------------------
    onPointerMove (e) {
      // Only trigger when feed is hidden
      if (this.navState !== 'off') {
        // feed is visible; treat as activity to keep it up
        this.resetAutoHideTimer()
        return
      }
      const now = performance.now()
      const { clientX: x, clientY: y } = e
      if (this._lastPointerX !== null && this._lastPointerTime !== null) {
        const dx = x - this._lastPointerX
        const dy = y - this._lastPointerY
        const dt = now - this._lastPointerTime || 1
        const distance = Math.hypot(dx, dy)
        const speed = distance / dt // px per ms
        // Consider movements above threshold (tuned empirically)
        if (speed > 1) {
          const currentVector = { x: dx, y: dy }
          if (this._lastVector) {
            // Dot product < 0 means direction reversal (back-and-forth)
            const dot = currentVector.x * this._lastVector.x + currentVector.y * this._lastVector.y
            if (dot < 0) {
              if (!this._firstReverseTime || (now - this._firstReverseTime) > 500) {
                // Start a new reversal window
                this._firstReverseTime = now
                this._reverseCount = 1
              } else {
                this._reverseCount++
                if (this._reverseCount >= 2) {
                  // Two quick reversals detected → show feed
                  this.updateNavState('on')
                  this.startAutoHideTimer()
                  // reset counters
                  this._reverseCount = 0
                  this._firstReverseTime = null
                }
              }
            }
          }
          this._lastVector = currentVector
        }
      }
      this._lastPointerX = x
      this._lastPointerY = y
      this._lastPointerTime = now
    },

    /* ---------------------------
     *  Auto-hide logic
     * --------------------------- */
    startAutoHideTimer () {
      this.clearAutoHideTimer()
      this._autoHideTimer = setTimeout(() => {
        if (this.navState !== 'off') {
          this.updateNavState('off')
        }
      }, this.autoHideDelay)
    },

    resetAutoHideTimer () {
      if (this._autoHideTimer) {
        this.clearAutoHideTimer()
        this.startAutoHideTimer()
      }
    },

    clearAutoHideTimer () {
      if (this._autoHideTimer) {
        clearTimeout(this._autoHideTimer)
        this._autoHideTimer = null
      }
    },

    onPostCellMouseEnter (e) {
      if (this.navState === 'off') return
      const postCell = e.target.closest('.post-cell')
      if (!postCell) return
      if (this._autoHideTimer) {
        this.autoHidePaused = true
        this.clearAutoHideTimer()
      }
    },

    onPostCellMouseLeave (e) {
      if (this.navState === 'off') return
      const postCell = e.target.closest('.post-cell')
      if (!postCell) return
      const relatedTarget = e.relatedTarget
      if (relatedTarget && relatedTarget.closest('.post-cell')) {
        return // Moving to another post cell, don't restart timer
      }
      if (this.autoHidePaused) {
        this.startAutoHideTimer()
        this.autoHidePaused = false
      }
    },

    onKeyDown (e) {
      if (e.key === 'Escape') {
        if (this.navState !== 'off') {
          this.updateNavState('off')
          this.clearAutoHideTimer()
        } else {
          this.updateNavState('on')
        }
      }
    },

    onFeedScroll () {
      // User is scrolling; keep feed visible
      if (this.navState !== 'off') {
        this.resetAutoHideTimer()
      }
    },

    onGlobalClick (e) {
      // Check if click is on a functional element
      const functionalElements = [
        'a', 'button', 'input', 'textarea', 'select', 'option',
        '[contenteditable]', '[tabindex]', '.post-cell', '.collapsable-text', '.clickable'
      ]
      // Check if the target or any parent is a functional element
      const isFunctionalClick = functionalElements.some(selector => {
        try {
          return e.target.closest(selector)
        } catch (err) {
          return false
        }
      })
      // Check if it's a text selection
      const selection = window.getSelection()
      const isTextSelection = selection && selection.toString().length > 0
      // If it's a functional click or text selection, don't interfere
      if (isFunctionalClick || isTextSelection) {
        return
      }
      // For clicks when navigation is active, check if click is within the wrapper
      // but not on any interactive elements (like post cells)
      if (this.navState !== 'off') {
        const isWithinWrapper = this.$refs['post-feed-wrap'].contains(e.target)
        if (!isWithinWrapper) {
          return
        }
        // Additional check: don't respond if clicking on canvas or other non-empty areas
        // where we want to allow normal interaction
      }
      // Only toggle navigation for "empty" clicks
      const newState = this.navState === 'off' ? '' : 'off'
      this.updateNavState(newState)
    },

    setParentSizeVars () {
      const parent = this.parentEl
      if (!parent) return

      // Clear cached measurements when parent changes
      this.clearLayoutCache()

      const { height, width } = parent.getBoundingClientRect()
      this.parentW = width || window.innerWidth
      this.parentH = height || window.innerHeight

      const fontSize = this.getLetterHeight()
      this.letterScale = fontSize / this.parentH
    },

    setFeedHeightStyle () {
      if (!this.points.length) {
        this.feedHeightStyle = { height: '100vh' }
        return Promise.resolve()
      }
      // Return a promise so we can wait for it to complete
      return new Promise((resolve) => {
        this.$nextTick(() => {
          const titleEl = document.querySelector('.post-cell-wrap .title-letter')
          const titleH = titleEl ? titleEl.getBoundingClientRect().height : 80 // fallback to textSize
          this.feedHeightStyle = { height: `${this.points.length * titleH}px` }
          resolve()
        })
      })
    },

    openEditor (post) {
      this.showEditor({
        type: post.type,
        value: post
      })
    },

    cellAbsolutePositionStyle (i) {
      if (!this.points[i]) return null
      const { x, yScaled } = { ...this.points[i] }
      return {
        top: `${yScaled * 100}%`,
        left: `${x * 100}%`
      }
    },

    cellOffsetPositionStyle (i) {
      if (!this.points[i]) return null
      const { offset } = { ...this.points[i] }
      let top, bottom
      if (offset.y < 0) {
        bottom = '100%'
      } else {
        top = offset.y > 0 ? '0' : '-50%'
      }
      const left = offset.x < 0 ? 'calc(-100% - var(--small-padding))' : 'var(--small-padding)'
      return {
        bottom,
        top,
        left
      }
    },

    closeEditor () {
      this.postToEdit = null
    },

    drawLines () {
      const parent = this.parentEl
      if (!parent) return

      const canvas = this.$refs.canvas
      if (!canvas) return

      const { width, height } = parent.getBoundingClientRect()
      const lastY = this.points.length ? this.points[this.points.length - 1].y : 1
      // Set canvas dimensions
      canvas.width = width * window.devicePixelRatio
      canvas.height = height * lastY * window.devicePixelRatio
      canvas.style.height = 100 * lastY + '%'

      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      // Draw lines using relative coordinates for auto-scaling
      let preX, preY
      this.points.forEach(point => {
        let { x, yScaled: y } = { ...point }
        x = x * canvas.width
        y = y * canvas.height / lastY

        ctx.moveTo(x, y)
        if (typeof preX !== 'undefined' && typeof preY !== 'undefined') {
          ctx.lineTo(preX, preY)
        }
        preX = x
        preY = y
      })

      ctx.strokeStyle = 'rgba(255, 255, 255, 1)'
      ctx.stroke()
    },

    scrollToActivePost () {
      this.$nextTick(() => {
        const activePostId = this.currentPostId || this.$route.params.id
        if (!activePostId) return
        const activePostCell = this.$el.querySelector('.post-cell-wrap .post-cell button.active')
        if (!activePostCell) return
        activePostCell.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      })
    }
  }
}
</script>

<template>
  <div ref="post-feed-wrap" class="post-feed-wrap" :class="navState">
    <nav ref="post-feed" class="post-feed-grid" :style="feedHeightStyle">
      <canvas ref="canvas" />
      <template v-if="!posts.length">
        <div class="flex items-center justify-center">
          <div v-if="processing" class="text-aba-blue">Loading...</div>
          <div v-else>Nothing there</div>
        </div>
      </template>
      <template v-else>
        <div v-for="(post, i) in posts" :key="post.id" class="post-cell-wrap"
          :class="{ 'has-active': currentPostId === post.id }"
          :style="{ ...cellAbsolutePositionStyle(i), height: textSize + 'px' }">
          <router-link v-if="post.type === 'back-button'" :to="{ name: 'home' }" class="back-button relative"
            :class="{ 'active': $route.name === 'home' }" :style="cellOffsetPositionStyle(i)">
            <span class="material-icons">north_west</span>
          </router-link>
          <post-cell v-else :post="post" :active="currentPostId === post.id" :style="cellOffsetPositionStyle(i)"
            @click="updateNavState(navState === 'off' ? 'on' : 'off')">
          </post-cell>
        </div>
      </template>
    </nav>
  </div>
</template>

<style lang="scss">
.post-feed-wrap {
  --transition-duration: 0.4s;
  --title-letter-font-size: 3.75rem;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  overscroll-behavior: contain;
  z-index: 100;
  backdrop-filter: blur(5px) brightness(0.5);
  transition:
    background-color calc(var(--transition-duration) * 2),
    backdrop-filter calc(var(--transition-duration) * 2),
    pointer-events 0s;

  &.off .post-cell {
    --color-action: rgba(255, 255, 255, 0.2);
    --color-action-focus: #4f4fff;
    --color-action-focus: color-mix(in lab, var(--color-action), blue 20%);
  }

  .post-feed-grid {
    box-sizing: content-box;

    width: calc(100vw - var(--base-size) * 2);
    max-width: 800px;
    min-height: calc(100vh - var(--base-size) * 2);
    position: relative;
    margin-left: auto;
    margin-right: auto;

    canvas {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: -1;
      transition: opacity var(--transition-duration), filter var(--transition-duration);
      pointer-events: none;
    }

    /* .post-cell-wrap:has(.active) {
      text-decoration: underline;
      color: white;

      &:hover {
        text-decoration-color: var(--color-aba-blue);
      }
    } */

    .post-cell-wrap {
      transition: opacity var(--transition-duration), filter var(--transition-duration);
      position: absolute;
      pointer-events: auto;

      .post-cell {
        position: relative;
      }
    }

    .back-button {
      pointer-events: auto;

      .material-icons {
        font-size: var(--title-letter-font-size);
      }
    }
  }

  &.off {
    background-color: transparent;
    backdrop-filter: none;
    overflow: hidden;
    pointer-events: none;

    canvas {
      opacity: 0.4;
      filter: blur(9px);
    }

    .post-cell-wrap:not(.has-active) {
      opacity: 0.7;
      filter: blur(7px);
      pointer-events: none;
      transition: opacity 0.3s ease-in-out, filter 0.3s ease-in-out;
    }

    .post-cell-wrap.has-active {
      pointer-events: auto;
    }
  }

  &:not(.off) {
    /* When navigation is active, allow pointer events for scrolling and interaction */
    pointer-events: auto;
  }
}
</style>
